import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { checkDeepScanEligibility } from '@/features/deep-analysis/services/deepAnalysisApi';

/**
 * Hook to check if the current user's tenant has Deep Scan enabled.
 * Caches the result for the session to avoid repeated API calls.
 */
export function useDeepScanEligibility() {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [isEligible, setIsEligible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || checkedRef.current) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function check() {
      try {
        const token = await getAccessToken();
        if (!token || cancelled) {
          setIsLoading(false);
          return;
        }

        const result = await checkDeepScanEligibility(token);
        if (!cancelled) {
          setIsEligible(result.deepScanEnabled);
          checkedRef.current = true;
        }
      } catch {
        // Silently fail — Deep Analysis nav item just won't show
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    check();
    return () => { cancelled = true; };
  }, [isAuthenticated, getAccessToken]);

  return { isEligible, isLoading };
}
